// client/src/pages/Landing/Landing.jsx
import { useState, useRef, useEffect } from "react";
import GCU_LOPE from "./GCU_Lope_267.webp";
import GCU_CAMPUS_BLUEPRINT from "./GCU_Campus_Blueprint_Isometric.png";
import "./Landing.css";
import axios from "axios";
import { DateTime } from "luxon";
import API_BASE_URL from "../../apiConfig";
import beepSuccessSound from "../../assets/beepSuccess.mp3";
import beepErrorSound from "../../assets/beepError.mp3";
import { useToast } from "../../context/ToastContext";

// Every kiosk toast shares these: centered, sized to read across the room, and
// deduped so a fast scanner replaces the previous message instead of stacking
// over the input.
const KIOSK = { size: "kiosk", placement: "center", dedupeKey: "kiosk" };

function SignedOutMessage() {
  return (
    <div>
      You're signed out. Thanks!
      <br />
      Please return your safety glasses.
      <img
        src={`${process.env.PUBLIC_URL}/static/imgs/safe.png`}
        // The sentence above already says this, so a real alt would be read twice.
        alt=""
        style={{ width: 200, height: "auto", display: "block", margin: "0 auto" }}
      />
    </div>
  );
}

// The attendance log is bookkeeping that happens after the sign-in or sign-out
// has already been recorded. Letting it throw would replace the message the
// person just read with a false "something went wrong", so its failures are
// swallowed and reported to the console instead.
const recordToLogs = async (write) => {
  try {
    await write();
  } catch (error) {
    console.error("Attendance log write failed:", error.message);
  }
};

export default function Landing() {
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);
  const successAudio = useRef(null);
  const errorAudio = useRef(null);

  // Initial focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Initialize and refresh audio objects daily
  useEffect(() => {
    const initAudio = () => {
      successAudio.current = new Audio(beepSuccessSound);
      errorAudio.current = new Audio(beepErrorSound);
      successAudio.current.preload = "auto";
      errorAudio.current.preload = "auto";
    };
    initAudio();
    const refreshInterval = setInterval(initAudio, 24 * 60 * 60 * 1000); // Daily refresh
    return () => clearInterval(refreshInterval);
  }, []);

  // Periodic client reset every 48 hours
  useEffect(() => {
    const reloadInterval = setInterval(() => {
      console.log("Reloading to reset client state");
      window.location.reload();
    }, 48 * 60 * 60 * 1000); // Every 48 hours
    return () => clearInterval(reloadInterval);
  }, []);

  const handleInputChange = (e) => setInput(e.target.value);

  const handleClick = async (e) => {
    e.preventDefault();
    if (isSubmitting || !input.trim()) return;
    setIsSubmitting(true);

    const dt = DateTime.now();
    const dateNow = `${dt.month}-${dt.day}-${dt.year}`;
    const studentId = input.trim();
    const payload = {
      date: dateNow,
      Login: dt,
      Logout: "Still Signed In",
      StudentID: studentId,
    };

    try {
      console.log("Cookies before request:", document.cookie); // Debug cookies
      const response = await axios.post(
        `/api/signinTracker?action=toggle&id=${studentId}`,
        {},
        {
          timeout: 10000,
          withCredentials: true,
          validateStatus: (status) => status >= 200 && status < 500,
        }
      );

      console.log("Response:", response.status, response.data);

      // validateStatus above lets everything below 500 resolve here rather than
      // throw, so 429 / 404 / 409 have to be handled as responses. Status is
      // checked before the body because the rate-limit reply is a {message}
      // object that matches none of the string branches, which is why a
      // rate-limited scan used to report "Unexpected response".
      if (response.status === 429) {
        toast.warning("One moment, that scanned twice. Try again in a second.", KIOSK);
        await playErrorSound();
      } else if (response.status === 409) {
        toast.warning("We're still processing your last scan. Try again in a moment.", KIOSK);
        await playErrorSound();
      } else if (response.data === "You are signed out!!") {
        toast.info(<SignedOutMessage />, { ...KIOSK, duration: 6000 });
        payload.Logout = dt;
        await recordToLogs(() =>
          axios.patch(`${API_BASE_URL}/logs?action=updateSignOut`, payload)
        );
        await playSuccessSound();
      } else if (response.data === "You are not registered yet") {
        toast.error("You're not registered yet. See a student worker to get set up.", KIOSK);
        await playErrorSound();
      } else if (response.data?.name) {
        toast.success(`Welcome back, ${response.data.name.split(" ")[0]}!`, KIOSK);

        payload.Name = response.data.name;
        await recordToLogs(async () => {
          const checkIfNewDay = await axios.post(`${API_BASE_URL}/logs?action=checkDate`, {
            date: dateNow,
          });
          if (checkIfNewDay.data === false) {
            await axios.post(`${API_BASE_URL}/logs?action=create`, {
              date: dateNow,
              signIns: [],
            });
          }
          await axios.patch(`${API_BASE_URL}/logs?action=addSignIn`, payload);
        });
        await playSuccessSound();
      } else {
        console.warn("Unexpected response:", response.data);
        toast.warning("Something unexpected happened. Please see a student worker.", KIOSK);
        await playErrorSound();
      }
    } catch (error) {
      console.error("API error:", error.message, error.response?.status);
      // Only network failures, the 10s timeout, and 5xx reach here now.
      if (!error.response) {
        toast.error("We couldn't reach the server. Tell a student worker.", KIOSK);
      } else {
        toast.error("Something went wrong. Please try again or see a student worker.", KIOSK);
      }
      await playErrorSound();
    } finally {
      setIsSubmitting(false);
      setInput("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const playSuccessSound = async () => {
    try {
      await successAudio.current.play();
    } catch (error) {
      console.error("Success sound failed:", error);
      successAudio.current = new Audio(beepSuccessSound);
      await successAudio.current.play();
    }
  };

  const playErrorSound = async () => {
    try {
      await errorAudio.current.play();
    } catch (error) {
      console.error("Error sound failed:", error);
      errorAudio.current = new Audio(beepErrorSound);
      await errorAudio.current.play();
    }
  };

  return (
    <form
      className="landing"
      onSubmit={handleClick}
      style={{ "--blueprint-image": `url(${GCU_CAMPUS_BLUEPRINT})` }}
    >
      <div className="overlay"></div>
      <div className="centerPieces">
        <div className="signin-card">
          <div className="brand-row">
            <img className="customeLOPE" src={GCU_LOPE} alt="Lopelogo" />
            <div>
              <h3 className="engineeringShop">Engineering Shop</h3>
              <p className="subtext">
                Enter your Student ID. Scanning once signs you in, scanning
                again signs you out.
              </p>
            </div>
          </div>
          <label className="input-label" htmlFor="text">
            Student ID
          </label>
          <input
            id="text"
            ref={inputRef}
            className="studentIdInput"
            autoComplete="off"
            type="text"
            placeholder="Enter your 7-digit ID"
            value={input}
            onChange={handleInputChange}
            disabled={isSubmitting}
          />
          <p className="helper-text">
            Keep your safety glasses on, return tools at the counter, and log
            out before you leave.
          </p>
        </div>
      </div>
      <div className="circle" style={{ bottom: "-19%", right: "-11%", backgroundColor: "#6b3bd1" }}></div>
      <div className="circle" style={{ bottom: "-33%", right: "4%", backgroundColor: "#402170" }}></div>
      <div className="circle" style={{ bottom: "-19%", left: "-10%", backgroundColor: "#f7d400" }}></div>    </form>
  );
}
